import { AutocompleteTypes, TextFieldTypes } from "@ionic/core";
import { InputHTMLAttributes, memo } from "react";
import { Control, FieldValues, Path } from "react-hook-form";
import LabelContainer from "./label-container";
import TextField from "./textfield";

type TextFieldWithLabelProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label: string;
  placeholder?: string;
  type?: TextFieldTypes;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  autocomplete?: AutocompleteTypes;
  ariaLabel?: string;
  id?: string;
  error?: string;
  containerClassName?: string;
  inputClassName?: string;
  showErrorMessage?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  /**
   * Enable scroll-to-focus behaviour (scrolls textfield to top 1/3 of viewport when focused)
   * Useful for preventing keyboard from covering the input on mobile
   */
  scrollToOnFocus?: boolean;
  /** Visual theme. "dark" = white label + transparent pill for dark backgrounds; "settings" = pill with purple border. */
  theme?: "light" | "dark" | "settings";
};

function TextFieldWithLabel<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  type,
  inputMode,
  autocomplete,
  ariaLabel,
  id,
  error,
  containerClassName,
  inputClassName,
  showErrorMessage = true,
  onFocus,
  onBlur,
  inputRef,
  scrollToOnFocus = false,
  theme = "light",
}: TextFieldWithLabelProps<T>) {
  return (
    <LabelContainer
      label={label}
      htmlFor={id ?? name}
      error={error}
      showErrorMessage={showErrorMessage}
      className={containerClassName}
      theme={theme}
    >
      <TextField
        control={control}
        name={name}
        placeholder={placeholder}
        type={type}
        inputMode={inputMode}
        autocomplete={autocomplete}
        aria-label={ariaLabel ?? label}
        id={id ?? name}
        className={inputClassName}
        isError={Boolean(error)}
        onFocus={onFocus}
        onBlur={onBlur}
        inputRef={inputRef}
        scrollToOnFocus={scrollToOnFocus}
        theme={theme}
      />
    </LabelContainer>
  );
}

export default memo(TextFieldWithLabel) as typeof TextFieldWithLabel;
